/**
 * JWT Token Utilities
 * Handles JWT token decoding, validation, and expiry checking
 */

export interface JWTPayload {
  exp?: number;
  iat?: number;
  sub?: string;
  [key: string]: any;
}

/**
 * Decode JWT token without verification (client-side only)
 * @param token JWT token string
 * @returns Decoded payload or null if invalid
 */
export function decodeJWT(token: string): JWTPayload | null {
  try {
    if (!token || typeof token !== 'string') {
      return null;
    }

    // Split the token into parts
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.warn('Invalid JWT format: Expected 3 parts, got', parts.length);
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];
    
    // Add padding if needed for base64 decoding
    const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
    
    // Decode base64
    const decodedPayload = atob(paddedPayload);
    
    // Parse JSON
    return JSON.parse(decodedPayload) as JWTPayload;
  } catch (error) {
    console.warn('Failed to decode JWT token:', error);
    return null;
  }
}

/**
 * Check if JWT token is expired
 * @param token JWT token string
 * @returns true if expired, false if valid, null if invalid token
 */
export function isTokenExpired(token: string): boolean | null {
  const payload = decodeJWT(token);
  
  if (!payload || !payload.exp) {
    return null; // Invalid token or no expiry
  }

  // JWT exp is in seconds, Date.now() is in milliseconds
  const currentTime = Math.floor(Date.now() / 1000);
  return payload.exp < currentTime;
}

/**
 * Get token expiry date
 * @param token JWT token string
 * @returns Date object or null if invalid
 */
export function getTokenExpiryDate(token: string): Date | null {
  const payload = decodeJWT(token);
  
  if (!payload || !payload.exp) {
    return null;
  }

  // Convert seconds to milliseconds
  return new Date(payload.exp * 1000);
}

/**
 * Get time until token expires in milliseconds
 * @param token JWT token string
 * @returns milliseconds until expiry, or null if invalid/expired
 */
export function getTimeUntilExpiry(token: string): number | null {
  const expiryDate = getTokenExpiryDate(token);
  
  if (!expiryDate) {
    return null;
  }

  const timeUntilExpiry = expiryDate.getTime() - Date.now();
  return timeUntilExpiry > 0 ? timeUntilExpiry : 0;
}

/**
 * Check if token will expire within specified minutes
 * @param token JWT token string
 * @param minutes Minutes to check ahead
 * @returns true if token expires within the specified time
 */
export function willTokenExpireSoon(token: string, minutes: number = 5): boolean {
  const timeUntilExpiry = getTimeUntilExpiry(token);
  
  if (timeUntilExpiry === null) {
    return true; // Treat invalid tokens as expiring soon
  }

  const minutesInMs = minutes * 60 * 1000;
  return timeUntilExpiry <= minutesInMs;
}

/**
 * Validate multiple tokens (authToken and hasuraToken)
 * @param authToken Main authentication token
 * @param hasuraToken Hasura GraphQL token
 * @returns Object with validation results
 */
export function validateTokens(authToken: string | null, hasuraToken: string | null) {
  const result = {
    authTokenValid: false,
    hasuraTokenValid: false,
    authTokenExpired: false,
    hasuraTokenExpired: false,
    anyTokenExpired: false,
    allTokensValid: false
  };

  if (authToken) {
    const authExpired = isTokenExpired(authToken);
    result.authTokenValid = authExpired === false;
    result.authTokenExpired = authExpired === true;
  }

  if (hasuraToken) {
    const hasuraExpired = isTokenExpired(hasuraToken);
    result.hasuraTokenValid = hasuraExpired === false;
    result.hasuraTokenExpired = hasuraExpired === true;
  }

  result.anyTokenExpired = result.authTokenExpired || result.hasuraTokenExpired;
  result.allTokensValid = result.authTokenValid && result.hasuraTokenValid;

  return result;
}